import { useState } from 'react'
import { AlertCircle, AlertTriangle, CheckCircle2, Download, Loader2, MapPinOff } from 'lucide-react'
import { useEditorStore } from '../../store/editorStore'
import { useUiStore } from '../../store/uiStore'
import { Panel } from '../common/Panel'
import { api } from '../../api'

const KIND_LABEL: Record<string, string> = { preview: 'プレビュー生成', render: '書き出し' }

export function JobToast() {
  const previewJob = useEditorStore((s) => s.previewJob)
  const renderJob = useEditorStore((s) => s.renderJob)
  const previewUrl = useEditorStore((s) => s.previewUrl)
  const autoEditWarnings = useEditorStore((s) => s.autoEditWarnings)
  const dismissAutoEditWarnings = useEditorStore((s) => s.dismissAutoEditWarnings)
  const deepLinkNotice = useEditorStore((s) => s.deepLinkNotice)
  const dismissDeepLinkNotice = useEditorStore((s) => s.dismissDeepLinkNotice)
  const timelineH = useUiStore((s) => s.panelSizes.timeline)
  // P10-3 担当A (docs/P10_PHASE3.md §2, JobToast is optional/任意): a
  // completed render had no toast at all before this — only 'running'/
  // 'queued'/'error' show below, and unlike `previewUrl` there was no
  // analogous "done" signal for render. Local dismiss (by job id) since
  // nothing else in the store ever resets `renderJob` back to null.
  const [dismissedRenderJobId, setDismissedRenderJobId] = useState<string | null>(null)

  const jobs = [previewJob, renderJob].filter(
    (j): j is NonNullable<typeof j> => !!j && (j.status === 'running' || j.status === 'queued' || j.status === 'error'),
  )
  const renderDone =
    renderJob?.status === 'done' && renderJob.resultPath && renderJob.id !== dismissedRenderJobId ? renderJob : null

  if (jobs.length === 0 && !previewUrl && !renderDone && autoEditWarnings.length === 0 && !deepLinkNotice) return null

  return (
    // m19 (REVIEW_v7.md MINOR, 2026-09-05): this used to sit at `bottom-4`
    // (16px off the viewport's own bottom edge) — Timeline.tsx renders at a
    // FIXED 200px height docked to the bottom of the screen regardless of
    // viewport width, so at narrower widths (1280px, DOM-verified against
    // `1280_08_timeline_after_autoedit.png`) this toast's fixed 288px width
    // covered the timeline's right edge (the last Segment block + EndCard
    // Overlay bar — exactly what a warning right after 自動編集 needs to sit
    // next to, not on top of). Docked above the Timeline strip instead
    // (200px + the same 16px gap) so it can never overlap it at any width.
    //
    // UI_MK2 1b: the Timeline strip is no longer a fixed 200px — its height
    // is now `panelSizes.timeline` (user-resizable, EditorScreen.tsx). Track
    // that instead of the old hardcoded 216 so this toast keeps sitting
    // exactly above the Timeline at any height the user drags it to.
    <div
      className="fixed right-4 flex flex-col gap-2 z-50 w-72"
      style={{ bottom: timelineH + 16 }}
      data-testid="job-toast-container"
    >
      {deepLinkNotice && (
        <Panel className="p-3 border-amber-500/40" data-testid="deep-link-notice-toast">
          <div className="flex items-center gap-2 text-xs">
            <MapPinOff size={14} className="text-amber-400 shrink-0" />
            <span className="break-words">{deepLinkNotice}</span>
            <button
              onClick={dismissDeepLinkNotice}
              className="ml-auto text-text-faint hover:text-text text-[11px] shrink-0"
            >
              閉じる
            </button>
          </div>
        </Panel>
      )}
      {jobs.map((job) => (
        <Panel key={job.id} className="p-3" data-testid="job-toast">
          <div className="flex items-center gap-2 text-xs mb-1.5">
            {job.status === 'error' ? (
              <AlertCircle size={14} className="text-accent-red shrink-0" />
            ) : (
              <Loader2 size={14} className="animate-spin text-accent-blue shrink-0" />
            )}
            <span className="font-medium">{KIND_LABEL[job.kind] ?? job.kind}</span>
            <span className="text-text-faint ml-auto">{Math.round(job.progress * 100)}%</span>
          </div>
          {job.status === 'error' ? (
            <div className="text-[11px] text-accent-red break-words">{job.error ?? '不明なエラー'}</div>
          ) : (
            <div className="h-1 rounded bg-ink-bg overflow-hidden">
              <div className="h-full bg-accent-blue transition-all" style={{ width: `${job.progress * 100}%` }} />
            </div>
          )}
        </Panel>
      ))}
      {previewUrl && (
        <Panel className="p-3" data-testid="preview-ready-toast">
          <div className="flex items-center gap-2 text-xs">
            <CheckCircle2 size={14} className="text-accent-blue shrink-0" />
            <span>プレビューの生成が完了しました</span>
          </div>
          <a
            href={previewUrl}
            target="_blank"
            rel="noreferrer"
            className="text-[11px] text-accent-blue hover:underline mt-1 inline-block"
          >
            開く
          </a>
        </Panel>
      )}
      {renderDone && (
        <Panel className="p-3" data-testid="render-done-toast">
          <div className="flex items-center gap-2 text-xs">
            <CheckCircle2 size={14} className="text-accent-blue shrink-0" />
            <span>書き出しが完了しました</span>
            <button
              onClick={() => setDismissedRenderJobId(renderDone.id)}
              className="ml-auto text-text-faint hover:text-text text-[11px] shrink-0"
            >
              閉じる
            </button>
          </div>
          <a
            href={api.jobDownloadUrl(renderDone.id)}
            download
            className="flex items-center gap-1 text-[11px] text-accent-blue hover:underline mt-1"
          >
            <Download size={11} />
            ダウンロード
          </a>
        </Panel>
      )}
      {autoEditWarnings.length > 0 && (
        <Panel className="p-3 border-amber-500/40" data-testid="auto-edit-warnings-toast">
          <div className="flex items-center gap-2 text-xs mb-1">
            <AlertTriangle size={14} className="text-amber-400 shrink-0" />
            <span className="font-medium">自動編集の警告 ({autoEditWarnings.length})</span>
            <button
              onClick={dismissAutoEditWarnings}
              className="ml-auto text-text-faint hover:text-text text-[11px]"
            >
              閉じる
            </button>
          </div>
          <div className="text-[11px] text-amber-200/90 break-words">
            {autoEditWarnings[0]}
            {autoEditWarnings.length > 1 ? ` ほか${autoEditWarnings.length - 1}件（型と尺タブに全件表示）` : ''}
          </div>
        </Panel>
      )}
    </div>
  )
}
